<template>
  <div>

  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { playerFromJson } from '@/models/PlayerModel';
import { PLAYER_REQUEST } from '@/store/actions/player';
import { useFiltersStore } from '../composables/useFiltersStore';
import { EReportContext } from '@/enums/reports';
import ResultFilter from '@/filters/ResultFilter';
import batterProfileDataParamsProvider from '../providers/batterProfileDataParamsProvider';
import pitcherProfileDataParamsProvider from '../providers/pitcherProfileDataParamsProvider';

const filtersStore = useFiltersStore('profile');

export default defineComponent({
  name: 'Base',

  inject: ['repo', 'debug'],

  props: {
    id: {
      type: Number,
      default: 0,
    },
    context: {
      type: String,
    },
    PlayerRepo: {
      default() {
        return this.repo.get('players');
      },
    },
    debug: {
      type: Boolean,
      default: false,
    },

    reportType: {
      type: String,
      default: 'base',
    },
  },

  provide() {
    return {
      context: computed(() => this.context),

      reportType: computed(() => this.reportType),

      player: computed(()=>this.player),

      query: computed(() => this.reportRequestQuery),

      staticFilters: computed(() => this.staticFilters),

      contextFilters: computed(() => this.contextFilters),
    };
  },

  data() {
    return {
      player: null,
      dateFilterRange: { min: null, max: null, },
      rankingStats: {
        batting: [],
        pitching: [],
      },
    };
  },

  created() {
    if (this.debug) {
      this.$log.debug('Base was created.');
    }
    filtersStore.ensure(this.id);
  },

  mounted() {
    if (this.id === 0) {
      this.error = 'Invalid player tag.';
    }
    if (this.debug) {
      this.$log.debug('Base was mounted.');
    }
  },

  computed: {
    ...mapState('playerProfiles', {
      allProfiles: 'profiles',
    }),

    ...mapGetters('playerProfiles', {
      getProfileById: 'getProfileById',
      getCurrentProfileStatus: 'getCurrentProfileStatus',
    }),

    staticFilters() {
      return filtersStore.getStatic(this.id);
    },

    dynamicFilters() {
      return filtersStore.getDynamic(this.id);
    },

    globalSeason() {
      return this.$store.state.app.filters.season;
    },

    view() {
      return [this.context, this.reportType].filter(v => v).join('-');
    },

    HitDataFilter() {
      return batterProfileDataParamsProvider(this.view);
    },

    PitchDataFilter() {
      return pitcherProfileDataParamsProvider(this.view);
    },

    storeStatus() {
      return this.getCurrentProfileStatus(this.id);
    },

    storeProfile() {
      return this.getProfileById(this.id).data;
    },

    reportRequestQuery() {
      if (!this.player) {
        return null;
      }
      return {
        Id: this.player.Id,
        HitDataFilter: this.HitDataFilter,
        PitchDataFilter: this.PitchDataFilter,
        Context: this.$route.params.context,
        Filters: this.staticFilters,
      };
    },

    contextFilters() {
      let contextFilter = '';
      let mappedFilters = {};

      switch (this.context) {
        case EReportContext.PITCHING.key:
          contextFilter = ResultFilter.Pitcher.filterKey;
          break;
        case EReportContext.BATTING.key:
          contextFilter = ResultFilter.Batter.filterKey;
          break;
        default:
        // NOOP
      }

      let runnerKey = ResultFilter.ScoringRunnersOn.filterKey;

      if (
        this.dynamicFilters.hasOwnProperty(runnerKey)
        && !Array.isArray(this.dynamicFilters[runnerKey])
        && this.dynamicFilters[runnerKey] !== ''
      ) {
        mappedFilters[runnerKey] = [this.dynamicFilters[runnerKey]];
      }
      return { [contextFilter]: this.id, ...this.dynamicFilters, ...mappedFilters };
    },
  },

  methods: {
    ...mapActions('playerProfiles', {
      fetchPlayerFromApi: PLAYER_REQUEST,
    }),

    setDefaultFilters() {
        if (this.player && this.player.hasTeam && this.player.teams.length === 1) {
          this.staticFilters.team = this.player.teams[0].TeamID;
        }
    },

    setData(err, playerData) {
      if (err) {
        this.error = err.toString();
      } else if (playerData) {
        this.player = playerFromJson(playerData);
        this.setDefaultFilters();
      }
    },

    fetchPlayer(newValue, oldValue) {
      this.$log.debug(this.$options.name, newValue, this.storeStatus, this.id, this.storeProfile);

      if (newValue !== null) {
        this.setData(undefined, newValue);
      }
    },

    async fetchData(newVal, oldVal) {
      this.$log.debug(this.$options.name, newVal, this.storeStatus, this.id, this.storeProfile);

      if (newVal && !this.storeProfile) {
        this.fetchPlayerFromApi(newVal);
      }

      this.rankingStats = await this.PlayerRepo.fetchRankingStats(newVal);
    },

    routeWatcher() {
      filtersStore.resetDynamic(this.id);
    },

    setDefaultDates() {
      const { startDate, endDate } = this.globalSeason;

      if (startDate) {
        this.staticFilters.seasonFrom = new Date(startDate).getFullYear();
        this.dateFilterRange.min = startDate;
      }
      if (endDate) {
        this.staticFilters.seasonTo = new Date(endDate).getFullYear();
        this.dateFilterRange.max = endDate;
      }
    },
  },

  watch: {
    id: {
      handler: 'fetchData',
      immediate: true,
    },

    storeProfile: {
      handler: 'fetchPlayer',
      immediate: true,
    },

    watchedRoute: {
      handler: 'routeWatcher',
      deep: true,
      immediate: true,
    },

    globalSeason: {
      handler: 'setDefaultDates',
      deep: true,
      immediate: true,
    },
  },
});
</script>
